import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Redirect } from '@reach/router';

import { useTranslation } from 'react-i18next';

import colors from 'lib/colors';
import safeStringLength from 'utils/safeStringLenght';

import { ReactComponent as Logo } from '../../../assets/clashing-logo-currentColor.svg';
import { ReactComponent as LogoEdu } from '../../../assets/clashing-edu-logo-currentColor-orange.svg';

import { Flex } from 'components';
import CompanyLogo from 'components/CompanyLogo';

import Header from '../../Header';
import { ProtectedRouteComponentProps } from '../../ProtectedRoute';

import { ScenesProgressProvider } from '../ScenesProgressProvider';
import LobbyBackgroundImages from './LobbyBackgroundImages';

type IsEduLayout = {
  isEduLayout?: boolean;
};

const Lobby: React.FunctionComponent<
  ProtectedRouteComponentProps & IsEduLayout
> = ({ user, isEduLayout, children }) => {
  const [t] = useTranslation();

  if (!user.tutorial.finished && !user.tutorial.closed && !user.skipTutorial) {
    return <Redirect to="/first-time-view" noThrow />;
  }

  return (
    <>
      <LobbyContainer>
        <Header />
        <Main>
          <Flex justifyContent="center">
            <LobbyCompanyLogo logo={user.company?.logo} />
            {/* This id is for a cannary login test, don't delete */}
            <WelcomeContainer
              id="lobby-welcome"
              css={{ color: colors.lobbyText }}
            >
              {isEduLayout ? (
                <LogoEdu width={260} height={52} css={{ marginTop: 2 }} />
              ) : (
                <Logo
                  css={css`
                    transform: scale(1.25);
                    margin-top: 2px;
                  `}
                />
              )}
              <WelcomeMessage>
                {t('lobby.welcome', 'vás víta, {{playerName}}', {
                  playerName: safeStringLength(user?.name || ''),
                })}
              </WelcomeMessage>
            </WelcomeContainer>
          </Flex>
          <ScenesProgressProvider user={user} isEduLayout={isEduLayout}>
            {children}
          </ScenesProgressProvider>
        </Main>
      </LobbyContainer>

      <LobbyBackgroundImages isEduLayout={isEduLayout} />
    </>
  );
};

export default Lobby;

export const Main = styled('main')({
  display: 'flex',
  position: 'relative',
  flexDirection: 'column',
  textAlign: 'center',
  width: '65%',
  maxWidth: '1100px',
  minHeight: '100vh',
  backgroundColor: colors.blueBackground,
  '@media screen and (max-width: 1279px)': {
    width: '85%',
  },
  '@media screen and (max-width: 1000px)': {
    position: 'static',
  },
  '& > *': {
    zIndex: 1,
  },
});

export const LobbyCompanyLogo = styled(CompanyLogo)({
  top: 0,
  left: '3em',
  position: 'absolute',
  '@media screen and (max-width: 1366px)': {
    left: 0,
  },
  '@media screen and (max-width: 1000px)': {
    left: '1em',
  },
});

export const LobbyContainer = styled('div')`
  color: ${colors.white};
  background-color: ${colors.darkBlueBackground};
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  position: relative;
  background: none;
`;

const WelcomeContainer = styled(Flex)({
  height: 90,
  alignItems: 'center',
  '@media screen and (max-width: 1279px)': {
    marginTop: 48,
  },
  '@media screen and (max-width: 800px)': {
    marginTop: 96,
  },
});

const WelcomeMessage = styled('span')({
  fontSize: 20,
});
